import React, { useEffect } from 'react';
import {
  Button,
  Drawer,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Switch,
  Tabs,
} from 'antd';
import AllocationRuleBuilder from './AllocationRuleBuilder';
import { useBatchConfig } from '../hooks/useBatchConfig';
import type { BatchConfigDTO } from '../types';

const { Option } = Select;
const { TextArea } = Input;

interface Props {
  open: boolean;
  config?: BatchConfigDTO | null;
  onClose: () => void;
  onSaved: (config: BatchConfigDTO) => void;
}

const BatchConfigForm: React.FC<Props> = ({ open, config, onClose, onSaved }) => {
  const [form] = Form.useForm();
  const { saving, createConfig, updateConfig } = useBatchConfig();
  const isEdit = !!config?.id;

  useEffect(() => {
    if (open) {
      if (config) {
        form.setFieldsValue(config);
      } else {
        form.resetFields();
        form.setFieldsValue({
          active: true,
          columnMapping: {
            headerRowCount: 1,
            delimiter: ',',
            memberIdType: 'MEMBER_NO',
            sheetIndex: 0,
            memberIdColumn: 0,
            amountColumn: 1,
          },
          allocationRules: [],
        });
      }
    }
  }, [open, config, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      // Re-index sequences
      if (values.allocationRules) {
        values.allocationRules = values.allocationRules.map(
          (rule: any, i: number) => ({ ...rule, sequence: i + 1 }),
        );
      }

      let saved: BatchConfigDTO | null;
      if (isEdit && config?.id) {
        saved = await updateConfig(config.id, values);
      } else {
        saved = await createConfig(values);
      }

      if (saved) {
        onSaved(saved);
        onClose();
      }
    } catch {
      // validation errors shown inline
    }
  };

  return (
    <Drawer
      title={isEdit ? `Edit Config: ${config?.name}` : 'New Batch Configuration'}
      open={open}
      onClose={onClose}
      width={720}
      destroyOnHidden
      footer={
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" loading={saving} onClick={handleSubmit}>
            {isEdit ? 'Update' : 'Create'}
          </Button>
        </div>
      }
    >
      <Form form={form} layout="vertical" size="middle">
        <Tabs
          items={[
            {
              key: 'basic',
              label: 'Basic Info',
              children: (
                <div className="space-y-0">
                  <div className="grid grid-cols-2 gap-4">
                    <Form.Item
                      name="code"
                      label="Config Code"
                      rules={[{ required: true, message: 'Code is required' }, { max: 20 }]}
                    >
                      <Input placeholder="e.g. SAV-BATCH-01" disabled={isEdit} />
                    </Form.Item>

                    <Form.Item
                      name="active"
                      label="Active"
                      valuePropName="checked"
                    >
                      <Switch />
                    </Form.Item>
                  </div>

                  <Form.Item
                    name="name"
                    label="Config Name"
                    rules={[{ required: true, message: 'Name is required' }, { max: 100 }]}
                  >
                    <Input placeholder="e.g. Monthly Savings Batch" />
                  </Form.Item>

                  <Form.Item name="description" label="Description">
                    <TextArea rows={2} maxLength={500} />
                  </Form.Item>
                </div>
              ),
            },
            {
              key: 'mapping',
              label: 'Column Mapping',
              children: (
                <div className="space-y-0">
                  <div className="grid grid-cols-2 gap-4">
                    <Form.Item
                      name={['columnMapping', 'memberIdType']}
                      label="Member ID Type"
                      rules={[{ required: true }]}
                    >
                      <Select>
                        <Option value="MEMBER_NO">Member No</Option>
                        <Option value="AC_NO">Account No</Option>
                      </Select>
                    </Form.Item>

                    <Form.Item
                      name={['columnMapping', 'delimiter']}
                      label="CSV Delimiter"
                      rules={[{ required: true }]}
                    >
                      <Select>
                        <Option value=",">, (comma)</Option>
                        <Option value=";">; (semicolon)</Option>
                        <Option value="|">| (pipe)</Option>
                        <Option value="\t">Tab</Option>
                      </Select>
                    </Form.Item>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <Form.Item
                      name={['columnMapping', 'headerRowCount']}
                      label="Header Rows to Skip"
                      rules={[{ required: true }]}
                    >
                      <InputNumber min={0} max={10} style={{ width: '100%' }} />
                    </Form.Item>

                    <Form.Item
                      name={['columnMapping', 'sheetIndex']}
                      label="Excel Sheet Index"
                      rules={[{ required: true }]}
                    >
                      <InputNumber min={0} style={{ width: '100%' }} />
                    </Form.Item>

                    <Form.Item
                      name={['columnMapping', 'dateFormat']}
                      label="Date Format"
                    >
                      <Input placeholder="e.g. yyyy-MM-dd" />
                    </Form.Item>
                  </div>

                  <div className="text-sm font-medium text-gray-600 mb-2">
                    Column Indexes (0-based)
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <Form.Item
                      name={['columnMapping', 'memberIdColumn']}
                      label="Member ID Column"
                      rules={[{ required: true }]}
                    >
                      <InputNumber min={0} style={{ width: '100%' }} />
                    </Form.Item>

                    <Form.Item
                      name={['columnMapping', 'amountColumn']}
                      label="Amount Column"
                      rules={[{ required: true }]}
                    >
                      <InputNumber min={0} style={{ width: '100%' }} />
                    </Form.Item>

                    <Form.Item
                      name={['columnMapping', 'accountNoColumn']}
                      label="Account No Column"
                    >
                      <InputNumber min={0} style={{ width: '100%' }} placeholder="optional" />
                    </Form.Item>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <Form.Item
                      name={['columnMapping', 'narrationColumn']}
                      label="Narration Column"
                    >
                      <InputNumber min={0} style={{ width: '100%' }} placeholder="optional" />
                    </Form.Item>

                    <Form.Item
                      name={['columnMapping', 'voucherColumn']}
                      label="Voucher Column"
                    >
                      <InputNumber min={0} style={{ width: '100%' }} placeholder="optional" />
                    </Form.Item>

                    <Form.Item
                      name={['columnMapping', 'transactionDateColumn']}
                      label="Date Column"
                    >
                      <InputNumber min={0} style={{ width: '100%' }} placeholder="optional" />
                    </Form.Item>
                  </div>
                </div>
              ),
            },
            {
              key: 'rules',
              label: 'Allocation Rules',
              children: (
                <div>
                  <div className="text-sm text-gray-500 mb-4">
                    Rules are applied in sequence order. Each row's amount is split according to these rules.
                    Use <strong>REMAINDER</strong> as the last rule to catch any leftover amount.
                  </div>
                  <AllocationRuleBuilder name="allocationRules" />
                </div>
              ),
            },
          ]}
        />
      </Form>
    </Drawer>
  );
};

export default BatchConfigForm;
